import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminService from "../services/AdminService";
import dayjs from "dayjs";
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area, ComposedChart, LabelList,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalUsers: 0,
        newUsers: 0,
        revenue: 0,
        liveAds: 0,
        supportQueue: 0,
        activeAds: 0,
        pendingAds: 0,
        soldAds: 0,
        expiredAds: 0,
        rejectedAds: 0,
        totalAds: 0,


        pendingReports: 0,
    });
    const [recentPosts, setRecentPosts] = useState([]);
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentPayments, setRecentPayments] = useState([]);
    const [recentSubscriptions, setRecentSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Analytics State
    const [dateRange, setDateRange] = useState("15d");
    const [customRange, setCustomRange] = useState({ start: "", end: "" });
    const [chartData, setChartData] = useState({
        userRegistrations: [],
        adsPosted: [],
        subscriptionPurchases: [],
        productsSold: []
    });
    const [statsLoading, setStatsLoading] = useState(false);

    useEffect(() => {
        loadDashboard();
    }, []);

    const getDatesFromRange = useCallback((range) => {
        let end = dayjs().format("YYYY-MM-DD");
        let start;

        switch (range) {
            case "today":
                start = dayjs().format("YYYY-MM-DD");
                break;
            case "7d":
                start = dayjs().subtract(7, "day").format("YYYY-MM-DD");
                break;
            case "15d":
                start = dayjs().subtract(15, "day").format("YYYY-MM-DD");
                break;
            case "30d":
                start = dayjs().subtract(30, "day").format("YYYY-MM-DD");
                break;
            case "3m":
                start = dayjs().subtract(3, "month").format("YYYY-MM-DD");
                break;
            case "custom":
                start = customRange.start;
                end = customRange.end;
                break;
            default:
                start = dayjs().subtract(7, "day").format("YYYY-MM-DD");
        }
        return { start, end };
    }, [customRange]);

    const loadStats = useCallback(async () => {
        const { start, end } = getDatesFromRange(dateRange);
        if (!start || !end) return;

        setStatsLoading(true);

        const fetchAnalytics = async (apiCall, key) => {
            try {
                const res = await apiCall(start, end);
                const data = res.data.data || [];
                setChartData(prev => ({ ...prev, [key]: data }));
            } catch (error) {
                console.error(`Error loading ${key} analytics:`, error);
                setChartData(prev => ({ ...prev, [key]: [] }));
            }


        };

        try {
            await Promise.all([
                fetchAnalytics(AdminService.getUserRegistrations, 'userRegistrations'),
                fetchAnalytics(AdminService.getSubscriptionPurchases, 'subscriptionPurchases'),
                fetchAnalytics(AdminService.getAdsPosted, 'adsPosted'),
                fetchAnalytics(AdminService.getProductsSold, 'productsSold')
            ]);
        } catch (error) {
            console.error("Critical error in analytics loading:", error);
        } finally {
            setStatsLoading(false);
        }
    }, [dateRange, getDatesFromRange]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleListingClick = (status) => {
        navigate(`/admin/ads?status=${status}`);
    };

    const loadDashboard = async () => {
        // Fetch KPI stats from dedicated endpoint
        let usersLoaded = false;
        try {
            const response = await AdminService.getKpiStats();
            if (response.data && response.data.data) {
                const kpiData = response.data.data;
                const backendStats = kpiData.stats || {};
                const listingStats = kpiData.listings || {};

                setStats({
                    ...backendStats,
                    activeAds: listingStats.activeAds || listingStats.active || 0,
                    pendingAds: listingStats.pendingAds || listingStats.pending || 0,
                    soldAds: listingStats.soldAds || listingStats.sold || 0,
                    expiredAds: listingStats.expiredAds || listingStats.expired || 0,
                    rejectedAds: listingStats.rejectedAds || listingStats.rejected || 0,
                    totalAds: listingStats.totalAds || listingStats.total || 0,
                    liveAds: listingStats.activeAds || listingStats.active || backendStats.liveAds || 0
                });
            }
        } catch (error) {
            console.error("KPI stats fetch failed:", error);
        }

        // Fetch pending reports count
        try {
            const reportsRes = await AdminService.getPendingReports();
            if (reportsRes.data && reportsRes.data.status) {
                setStats(prev => ({ ...prev, pendingReports: reportsRes.data.data.length }));
            }
        } catch (error) {
            console.log("Pending reports API not available");
        }

        // Fetch recent posts
        try {
            const postsRes = await AdminService.getRecentPosts();
            if (postsRes.data && postsRes.data.data) {
                const responseData = postsRes.data.data;
                if (Array.isArray(responseData)) {
                    setRecentPosts(responseData);
                } else if (responseData.RecentPosts) {
                    setRecentPosts(responseData.RecentPosts);
                }
            }
        } catch (error) {
            console.log("Recent posts API not available");
        }

        // Fetch recent sells
        try {
            const sellsRes = await AdminService.getRecentSells();
            if (sellsRes.data && sellsRes.data.data) {
                setRecentPayments(sellsRes.data.data);
            }
        } catch (error) {
            console.log("Recent sells API not available");
        }

        // Fetch recent subscriptions
        try {
            const subsRes = await AdminService.getRecentSubscriptions();
            if (subsRes.data && subsRes.data.data) {
                setRecentSubscriptions(subsRes.data.data);
            }
        } catch (error) {
            console.log("Recent subscriptions API not available");
        }

        // Fetch recent users from the dedicated endpoint
        try {
            const usersRes = await AdminService.getRecentUsers();
            if (usersRes.data && usersRes.data.data) {
                setRecentUsers(usersRes.data.data);
                usersLoaded = true;
            }
        } catch (error) {
            console.log("Recent 5 Users API not available, falling back to all users search");
        }

        // Fetch recent users if not already loaded from dashboard or dedicated endpoint
        if (!usersLoaded) {
            try {
                const usersRes = await AdminService.getUsers({ page: 0, size: 5, sort: "createdAt,desc" });
                if (usersRes.data && usersRes.data.data) {
                    const usersData = usersRes.data.data;
                    if (Array.isArray(usersData)) {
                        setRecentUsers(usersData);
                    } else if (usersData.content) {
                        setRecentUsers(usersData.content);
                    }
                }
            } catch (error) {
                console.log("Users API not available");
            }
        }

        setLoading(false);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffHrs < 1) return "Just now";
        if (diffHrs < 24) return `${diffHrs}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    const safeName = (str) => {
        if (!str) return "";
        const div = document.createElement("div");
        div.textContent = str;
        return div.innerHTML;
    };

    const getInitials = (name) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((w) => w[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    const avatarColors = [
        "linear-gradient(135deg, #2a89ba, #0083ca)",
        "linear-gradient(135deg, #28a745, #20c997)",
        "linear-gradient(135deg, #1d1c1bff, #fd7e14)",
        "linear-gradient(135deg, #dc3545, #e83e8c)",
        "linear-gradient(135deg, #6f42c1, #5a67d8)",
    ];

    const getPostImage = (post) => {
        if (post.productImages && post.productImages.length > 0) {
            return post.productImages[0].imageUrl;
        }
        return null;
    };

    const getLocationText = (post) => {
        if (!post.productAddresses) return "";
        const parts = [post.productAddresses.city, post.productAddresses.state].filter(Boolean);
        return parts.join(", ");
    };

    const getPostStatus = (post) => {
        if (post.sold) return { label: "Sold", className: "expired" };
        if (post.isBlocked) return { label: "Blocked", className: "failed" };
        if (!post.status) return { label: "Pending", className: "pending" };
        if (post.expireDate && new Date(post.expireDate) < new Date()) return { label: "Expired", className: "expired" };
        return { label: "Active", className: "active" };
    };

    if (loading) {
        return (
            <div className="admin-empty-state">
                <i className="fa fa-spinner fa-spin"></i>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Main Primary KPIs */}
            <div className="admin-kpi-grid">
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon users"><i className="fa fa-users"></i></div>
                    <div className="admin-kpi-info">
                        <h4>{stats.totalUsers?.toLocaleString()}</h4>
                        <p>Total Users</p>
                        <small>+{stats.newUsers} new (30d)</small>
                    </div>
                </div>
                <div className="admin-kpi-card" onClick={() => handleListingClick('ALL')} style={{ cursor: 'pointer' }}>
                    <div className="admin-kpi-icon ads"><i className="fa fa-bullhorn"></i></div>
                    <div className="admin-kpi-info">
                        <h4>{stats.totalAds?.toLocaleString()}</h4>
                        <p>Total Listings</p>
                        <small>All items</small>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon revenue"><i className="fa fa-dollar"></i></div>
                    <div className="admin-kpi-info">
                        <h4>${stats.revenue?.toLocaleString()}</h4>
                        <p>Total Revenue</p>
                        <small>Top-ups & Subscriptions</small>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon support"><i className="fa fa-life-ring"></i></div>
                    <div className="admin-kpi-info">
                        <h4>{stats.supportQueue}</h4>
                        <p>Support Queue</p>
                        <small>Pending messages</small>
                    </div>
                </div>
                <div className="admin-kpi-card" onClick={() => navigate("/admin/reports")} style={{ cursor: 'pointer' }}>
                    <div className="admin-kpi-icon" style={{ background: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}><i className="fa fa-flag"></i></div>
                    <div className="admin-kpi-info">
                        <h4>{stats.pendingReports}</h4>
                        <p>Pending Reports</p>
                        <small>Requires review</small>
                    </div>
                </div>
            </div>

            {/* Listing Status Distribution */}
            <div className="admin-section-header" style={{ marginTop: '35px', marginBottom: '15px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--admin-text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="fa fa-th-large" style={{ color: 'var(--admin-primary)', opacity: 0.8 }}></i>
                    Listing Status Breakdown
                </h3>
                <p style={{ fontSize: '12px', color: 'var(--admin-text-muted)', margin: '4px 0 0 28px' }}>Real-time overview of ad performance and moderation queue</p>
            </div>

            <div className="admin-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '35px' }}>
                <div className="admin-kpi-card" onClick={() => handleListingClick('ACTIVE')} style={{ padding: '15px', borderLeft: '4px solid #10b981', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    <div className="admin-kpi-info">
                        <h4 style={{ fontSize: '20px', marginBottom: '2px' }}>{stats.activeAds?.toLocaleString()}</h4>
                        <p style={{ fontSize: '12px', fontWeight: '500' }}>Active Ads</p>
                        <small style={{ color: '#10b981', fontSize: '10px' }}>Currently Live</small>
                    </div>
                    <i className="fa fa-check-circle" style={{ position: 'absolute', right: '-5px', bottom: '-5px', fontSize: '40px', opacity: 0.05, color: '#10b981' }}></i>
                </div>

                <div className="admin-kpi-card" onClick={() => handleListingClick('PENDING')} style={{ padding: '15px', borderLeft: '4px solid #f59e0b', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    <div className="admin-kpi-info">
                        <h4 style={{ fontSize: '20px', marginBottom: '2px' }}>{stats.pendingAds?.toLocaleString()}</h4>
                        <p style={{ fontSize: '12px', fontWeight: '500' }}>Pending Review</p>
                        <small style={{ color: '#f59e0b', fontSize: '10px' }}>Awaiting Approval</small>
                    </div>
                    <i className="fa fa-clock-o" style={{ position: 'absolute', right: '-5px', bottom: '-5px', fontSize: '40px', opacity: 0.05, color: '#f59e0b' }}></i>
                </div>

                <div className="admin-kpi-card" onClick={() => handleListingClick('SOLD')} style={{ padding: '15px', borderLeft: '4px solid #3b82f6', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    <div className="admin-kpi-info">
                        <h4 style={{ fontSize: '20px', marginBottom: '2px' }}>{stats.soldAds?.toLocaleString()}</h4>
                        <p style={{ fontSize: '12px', fontWeight: '500' }}>Sold Items</p>
                        <small style={{ color: '#3b82f6', fontSize: '10px' }}>Marked as Sold</small>
                    </div>
                    <i className="fa fa-shopping-bag" style={{ position: 'absolute', right: '-5px', bottom: '-5px', fontSize: '40px', opacity: 0.05, color: '#3b82f6' }}></i>
                </div>

                <div className="admin-kpi-card" onClick={() => handleListingClick('EXPIRED')} style={{ padding: '15px', borderLeft: '4px solid #64748b', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    <div className="admin-kpi-info">
                        <h4 style={{ fontSize: '20px', marginBottom: '2px' }}>{stats.expiredAds?.toLocaleString()}</h4>
                        <p style={{ fontSize: '12px', fontWeight: '500' }}>Expired Ads</p>
                        <small style={{ color: '#64748b', fontSize: '10px' }}>Past Expiry Date</small>
                    </div>
                    <i className="fa fa-calendar-times-o" style={{ position: 'absolute', right: '-5px', bottom: '-5px', fontSize: '40px', opacity: 0.05, color: '#64748b' }}></i>
                </div>

                <div className="admin-kpi-card" onClick={() => handleListingClick('REJECTED')} style={{ padding: '15px', borderLeft: '4px solid #ef4444', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    <div className="admin-kpi-info">
                        <h4 style={{ fontSize: '20px', marginBottom: '2px' }}>{stats.rejectedAds?.toLocaleString()}</h4>
                        <p style={{ fontSize: '12px', fontWeight: '500' }}>Rejected</p>
                        <small style={{ color: '#ef4444', fontSize: '10px' }}>Violated Policy</small>
                    </div>
                    <i className="fa fa-ban" style={{ position: 'absolute', right: '-5px', bottom: '-5px', fontSize: '40px', opacity: 0.05, color: '#ef4444' }}></i>
                </div>
            </div>

            {/* Analytics Section */}
            <div className="admin-stats-section" style={{ marginBottom: 30 }}>
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3><i className="fa fa-line-chart" style={{ marginRight: 8, color: "var(--admin-primary)" }}></i> Platform Analytics</h3>
                        <div className="admin-filters">
                            <select
                                className="admin-filter-select"
                                value={dateRange}
                                onChange={(e) => setDateRange(e.target.value)}
                                style={{ minWidth: 150 }}
                            >
                                <option value="today">Today</option>
                                <option value="7d">Last 7 Days</option>
                                <option value="15d">Last 15 Days</option>
                                <option value="30d">Last 30 Days</option>
                                <option value="3m">Last 3 Months</option>
                                <option value="custom">Custom Range</option>
                            </select>
                            {dateRange === "custom" && (
                                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                    <input
                                        type="date"
                                        className="admin-form-control admin-form-control-sm"
                                        value={customRange.start}
                                        onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                                    />
                                    <span>to</span>
                                    <input
                                        type="date"
                                        className="admin-form-control admin-form-control-sm"
                                        value={customRange.end}
                                        onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                                    />
                                    <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={loadStats}>Apply</button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="admin-card-body" style={{ minHeight: 400, position: 'relative' }}>
                        <div className="admin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: 20 }}>
                            {/* 1. User Registrations - BarChart (Blue) */}
                            <div className="admin-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#333' }}>
                                        <i className="fa fa-users" style={{ color: '#3b82f6', marginRight: 10 }}></i>
                                        User Registrations
                                    </h4>
                                    <span style={{ fontSize: 11, background: '#eff6ff', color: '#3b82f6', padding: '4px 8px', borderRadius: 4, fontWeight: 500 }}>NEW MEMBERS</span>
                                </div>
                                <div style={{ width: '100%', height: 280 }}>
                                    {statsLoading ? (
                                        <div className="skeleton-chart" style={{ height: '100%', width: '100%', background: 'linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'skeleton-pulse 1.5s infinite', borderRadius: 8 }}></div>
                                    ) : chartData.userRegistrations.length > 0 ? (
                                        <ResponsiveContainer>
                                            <BarChart data={chartData.userRegistrations} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                                <Tooltip
                                                    cursor={{ fill: '#f1f5f9', opacity: 0.4 }}
                                                    contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', padding: '8px 12px' }}
                                                />
                                                <Bar dataKey="count" name="Registrations" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={24} animationDuration={1200} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="no-data-placeholder" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                            <i className="fa fa-user-times fa-3x" style={{ marginBottom: 15, opacity: 0.15 }}></i>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>No registrations found</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. Subscription Purchases - BarChart (Orange) */}
                            <div className="admin-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#333' }}>
                                        <i className="fa fa-diamond" style={{ color: '#f59e0b', marginRight: 10 }}></i>
                                        Subscriptions
                                    </h4>
                                    <span style={{ fontSize: 11, background: '#fff7ed', color: '#f59e0b', padding: '4px 8px', borderRadius: 4, fontWeight: 500 }}>REVENUE</span>
                                </div>
                                <div style={{ width: '100%', height: 280 }}>
                                    {statsLoading ? (
                                        <div className="skeleton-chart" style={{ height: '100%', width: '100%', background: 'linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'skeleton-pulse 1.5s infinite', borderRadius: 8 }}></div>
                                    ) : chartData.subscriptionPurchases.length > 0 ? (
                                        <ResponsiveContainer>
                                            <BarChart data={chartData.subscriptionPurchases} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                                <Tooltip
                                                    cursor={{ fill: '#fff7ed', opacity: 0.4 }}
                                                    contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                                />
                                                <Bar dataKey="count" name="Purchases" fill="#f59e0b" radius={[6, 6, 0, 0]} barSize={20} animationDuration={1200} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="no-data-placeholder" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                            <i className="fa fa-credit-card-alt fa-3x" style={{ marginBottom: 15, opacity: 0.15 }}></i>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>No subscriptions sold</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 3. Ads Posted - LineChart (Purple, Smooth) */}
                            <div className="admin-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#333' }}>
                                        <i className="fa fa-bullhorn" style={{ color: '#8b5cf6', marginRight: 10 }}></i>
                                        Ads Posted
                                    </h4>
                                    <span style={{ fontSize: 11, background: '#f5f3ff', color: '#8b5cf6', padding: '4px 8px', borderRadius: 4, fontWeight: 500 }}>ACTIVITY</span>
                                </div>
                                <div style={{ width: '100%', height: 280 }}>
                                    {statsLoading ? (
                                        <div className="skeleton-chart" style={{ height: '100%', width: '100%', background: 'linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'skeleton-pulse 1.5s infinite', borderRadius: 8 }}></div>
                                    ) : chartData.adsPosted.length > 0 ? (
                                        <ResponsiveContainer>
                                            <LineChart data={chartData.adsPosted} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                                <Legend verticalAlign="top" align="right" height={30} iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500 }} />
                                                <Line
                                                    type="monotone"
                                                    dataKey="count"
                                                    name="Ads"
                                                    stroke="#8b5cf6"
                                                    strokeWidth={4}
                                                    dot={{ r: 5, fill: '#8b5cf6', strokeWidth: 2, stroke: '#fff' }}
                                                    activeDot={{ r: 8, strokeWidth: 0, fill: '#8b5cf6' }}
                                                    animationDuration={1500}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="no-data-placeholder" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                            <i className="fa fa-clipboard fa-3x" style={{ marginBottom: 15, opacity: 0.15 }}></i>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>No activity recorded</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 4. Products Sold - AreaChart (Green) */}
                            <div className="admin-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#333' }}>
                                        <i className="fa fa-shopping-bag" style={{ color: '#10b981', marginRight: 10 }}></i>
                                        Marketplace Sales
                                    </h4>
                                    <span style={{ fontSize: 11, background: '#ecfdf5', color: '#10b981', padding: '4px 8px', borderRadius: 4, fontWeight: 500 }}>CLOSED DEALS</span>
                                </div>
                                <div style={{ width: '100%', height: 280 }}>
                                    {statsLoading ? (
                                        <div className="skeleton-chart" style={{ height: '100%', width: '100%', background: 'linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'skeleton-pulse 1.5s infinite', borderRadius: 8 }}></div>
                                    ) : chartData.productsSold.length > 0 ? (
                                        <ResponsiveContainer>
                                            <ComposedChart data={chartData.productsSold} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                                <Legend verticalAlign="top" align="right" height={36} />
                                                <Bar dataKey="count" name="Daily Sales" fill="#16a34a" radius={[4, 4, 0, 0]} barSize={30} animationDuration={1200}>
                                                    <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#16a34a' }} />
                                                </Bar>
                                                <Line type="monotone" dataKey="count" name="Trend" stroke="#059669" strokeWidth={3} dot={{ r: 4, fill: '#fff', strokeWidth: 2, stroke: '#059669' }} activeDot={{ r: 6 }} animationDuration={1500} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="no-data-placeholder" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                            <i className="fa fa-shopping-cart fa-3x" style={{ marginBottom: 15, opacity: 0.15 }}></i>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>No sales data available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══════════ RECENT POSTS (with images) ═══════════ */}
            <div className="admin-card">
                <div className="admin-card-header">
                    <h3><i className="fa fa-bullhorn" style={{ marginRight: 8, color: "var(--admin-accent)" }}></i>Recent Posts</h3>
                    <Link to="/admin/ads" className="admin-btn admin-btn-primary admin-btn-sm">
                        View All Ads <i className="fa fa-arrow-right" style={{ marginLeft: 4, fontSize: 10 }}></i>
                    </Link>
                </div>
                <div className="admin-card-body">
                    {recentPosts.length === 0 ? (
                        <div className="admin-empty-state" style={{ padding: "40px 20px" }}>
                            <i className="fa fa-bullhorn" style={{ fontSize: 36, color: "var(--admin-text-muted)" }}></i>
                            <p>No recent posts available</p>
                        </div>
                    ) : (
                        <div className="admin-posts-grid">
                            {recentPosts.slice(0, 5).map((post) => {
                                const postStatus = getPostStatus(post);
                                return (
                                    <div className="admin-post-card" key={post.id}>
                                        {/* Image area */}
                                        <div className="admin-post-card-img">
                                            {getPostImage(post) ? (
                                                <img
                                                    src={getPostImage(post)}
                                                    alt={post.title || "Post"}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.style.display = "none";
                                                        e.target.parentElement.classList.add("no-img");
                                                    }}
                                                />
                                            ) : (
                                                <div className="admin-post-card-img-placeholder">
                                                    <i className="fa fa-image"></i>
                                                </div>
                                            )}
                                            {/* Badges overlay */}
                                            <div className="admin-post-card-overlay">
                                                <span className={`admin-badge ${postStatus.className}`}>
                                                    {postStatus.label}
                                                </span>
                                                {post.featured && (
                                                    <span className="admin-badge featured">
                                                        <i className="fa fa-star"></i> Featured
                                                    </span>
                                                )}
                                            </div>
                                            {/* Image count */}
                                            {post.productImages && (Array.isArray(post.productImages) ? post.productImages : Array.from(post.productImages)).length > 1 && (
                                                <span className="admin-post-card-imgcount">
                                                    <i className="fa fa-camera"></i> {(Array.isArray(post.productImages) ? post.productImages : Array.from(post.productImages)).length}
                                                </span>
                                            )}
                                        </div>
                                        {/* Card content */}
                                        <div className="admin-post-card-body">
                                            <div className="admin-post-card-cat">
                                                {post.category?.parentCategory?.name
                                                    ? `${post.category.parentCategory.name} › ${post.category.name}`
                                                    : post.category?.name || "Uncategorized"}
                                            </div>
                                            <h4 className="admin-post-card-title" title={post.title}>
                                                {safeName(post.title) || "Untitled Post"}
                                            </h4>
                                            <div className="admin-post-card-price">{post.price || ""}</div>
                                            {/* Footer */}
                                            <div className="admin-post-card-footer">
                                                <span><i className="fa fa-user-circle"></i> {safeName(post.userResponse?.name) || "Unknown"}</span>
                                                <span><i className="fa fa-clock-o"></i> {formatDate(post.createdAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ═══════════ RECENT USERS ═══════════ */}
            <div className="admin-card">
                <div className="admin-card-header">
                    <h3><i className="fa fa-user-plus" style={{ marginRight: 8, color: "var(--admin-primary)" }}></i>Recent Users</h3>
                    <Link to="/admin/users" className="admin-btn admin-btn-primary admin-btn-sm">
                        View All Users <i className="fa fa-arrow-right" style={{ marginLeft: 4, fontSize: 10 }}></i>
                    </Link>
                </div>
                <div className="admin-card-body">
                    {recentUsers.length === 0 ? (
                        <div className="admin-empty-state" style={{ padding: "30px 20px" }}>
                            <i className="fa fa-users"></i>
                            <p>No recent users</p>
                        </div>
                    ) : (
                        <div className="admin-posts-grid">
                            {recentUsers.slice(0, 5).map((user, idx) => (
                                <div className="admin-post-card" key={user.id || idx}>
                                    <div className="admin-post-card-img">
                                        {user.imageUrl ? (
                                            <img
                                                src={user.imageUrl}
                                                alt={user.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                    e.target.parentElement.classList.add("no-img");
                                                }}
                                            />
                                        ) : (
                                            <div className="admin-post-card-img-placeholder">
                                                <i className="fa fa-user-circle" style={{ fontSize: 60, opacity: 0.2 }}></i>
                                            </div>
                                        )}
                                        <div className="admin-post-card-overlay">
                                            <span className="admin-badge active" style={{ fontSize: 9 }}>MEMBER</span>
                                        </div>
                                    </div>
                                    <div className="admin-post-card-body">
                                        <div className="admin-post-card-cat">
                                            User ID: #{user.id}
                                        </div>
                                        <h4 className="admin-post-card-title" title={user.name}>
                                            {safeName(user.name)}
                                        </h4>
                                        <div className="admin-user-card-email" style={{ fontSize: 11, marginBottom: 5 }}>
                                            <i className="fa fa-envelope-o"></i> {user.email}
                                        </div>
                                        <div className="admin-user-card-phone" style={{ fontSize: 11 }}>
                                            <i className="fa fa-phone"></i> {user.mobile || <em>No phone</em>}
                                        </div>
                                        <div className="admin-post-card-footer" style={{ marginTop: "auto", paddingTop: 10 }}>
                                            <span><i className="fa fa-calendar"></i> Joined {formatDate(user.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Two-column grid for Sells & Subscriptions */}
            <div className="admin-dashboard-grid">
                {/* Recent Sells */}
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3><i className="fa fa-money" style={{ marginRight: 8, color: "var(--admin-success)" }}></i>Recent Sells</h3>
                        <Link to="/admin/sells" className="admin-btn admin-btn-outline admin-btn-sm">
                            View All <i className="fa fa-arrow-right" style={{ marginLeft: 4, fontSize: 10 }}></i>
                        </Link>
                    </div>
                    <div className="admin-card-body admin-card-body-flush">
                        {recentPayments.length === 0 ? (
                            <div className="admin-empty-state" style={{ padding: "40px 20px" }}>
                                <i className="fa fa-money" style={{ fontSize: 32, color: "var(--admin-text-muted)", opacity: 0.5 }}></i>
                                <p>No recent sells</p>
                            </div>
                        ) : (
                            <div className="admin-recent-list">
                                {recentPayments.slice(0, 5).map((payment) => (
                                    <div className="admin-recent-item" key={payment.sellId}>
                                        <div className="admin-recent-item-icon coin-icon">
                                            <i className="fa fa-shopping-cart"></i>
                                        </div>
                                        <div className="admin-recent-item-info">
                                            <div className="admin-recent-item-title">{payment.postTitle || "Transaction"}</div>
                                            <div className="admin-recent-item-sub">
                                                <span style={{ color: "var(--admin-text-muted)", fontSize: 11 }}>
                                                    <i className="fa fa-user" style={{ marginRight: 3 }}></i>{payment.buyerName || "Unknown"}
                                                </span>
                                                <span className={`admin-badge ${payment.status === "Completed" ? "active" : "failed"}`}>
                                                    {payment.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="admin-recent-item-right">
                                            <div className="admin-recent-item-amount" style={{ color: "#28a745" }}>
                                                ${payment.amount}
                                            </div>
                                            <div className="admin-recent-item-time">{formatDate(payment.transactionDate)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Subscriptions */}
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3><i className="fa fa-credit-card" style={{ marginRight: 8, color: "#6f42c1" }}></i>Recent Subscriptions</h3>
                        <Link to="/admin/subscriptions" className="admin-btn admin-btn-outline admin-btn-sm">
                            View All <i className="fa fa-arrow-right" style={{ marginLeft: 4, fontSize: 10 }}></i>
                        </Link>
                    </div>
                    <div className="admin-card-body admin-card-body-flush">
                        {recentSubscriptions.length === 0 ? (
                            <div className="admin-empty-state" style={{ padding: "40px 20px" }}>
                                <i className="fa fa-credit-card" style={{ fontSize: 32, color: "var(--admin-text-muted)", opacity: 0.5 }}></i>
                                <p>No recent subscriptions</p>
                            </div>
                        ) : (
                            <div className="admin-recent-list">
                                {recentSubscriptions.slice(0, 5).map((sub) => (
                                    <div className="admin-recent-item subscription-row" key={sub.subscriptionId}>
                                        <div className="admin-recent-item-icon subscription-icon">
                                            <i className="fa fa-diamond"></i>
                                        </div>
                                        <div className="admin-recent-item-info">
                                            <div className="admin-recent-item-title">
                                                {sub.userName || "Unknown"}
                                                <span className="admin-plan-tag">{sub.planName || "Plan"}</span>
                                            </div>
                                            <div className="admin-recent-item-sub">
                                                <span className={`admin-badge ${sub.status === "Active" ? "active" : "expired"}`}>
                                                    {sub.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="admin-recent-item-right">
                                            <div className="admin-recent-item-amount" style={{ color: "#6f42c1" }}>
                                                ${sub.amount}
                                            </div>
                                            <div className="admin-recent-item-time">{formatDate(sub.startDate)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
